import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import CustomerAutoComplete from '../../components/CustomerAutoComplete';
import SupplierAutoComplete from '../../components/SupplierAutoComplete';
import SettlementService from '../../services/SettlementService';
import { customerService } from '../../services/CustomerService';
import SupplierService from '../../services/SupplierService';
import { useNavigate } from 'react-router-dom';

const SettlementPage = () => {
    const [mounted, setMounted] = useState(false);
    const [customerId, setCustomerId] = useState(null);
    const [supplierId, setSupplierId] = useState(null);
    const [amount, setAmount] = useState(0);
    const [date, setDate] = useState(new Date());
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    const [customerDetail, setCustomerDetail] = useState(null);
    const [supplierDetail, setSupplierDetail] = useState(null);

    const toast = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (customerId) {
            customerService.getCustomerDetail(customerId).then(res => {
                if (res.data.success) {
                    setCustomerDetail(res.data.data);
                    if (res.data.data.customer.relatedSupplierId && !supplierId) {
                        setSupplierId(res.data.data.customer.relatedSupplierId);
                    }
                }
            });
        } else {
            setCustomerDetail(null);
        }
    }, [customerId]);

    useEffect(() => {
        if (supplierId) {
            SupplierService.getSupplierDetail(supplierId).then(data => {
                setSupplierDetail(data);
                if (data.supplier.relatedCustomerId && !customerId) {
                    setCustomerId(data.supplier.relatedCustomerId);
                }
            });
        } else {
            setSupplierDetail(null);
        }
    }, [supplierId]);

    const handleSave = async () => {
        if (!customerId || !supplierId || amount <= 0) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen müşteri, tedarikçi ve geçerli bir tutar giriniz.', life: 3000 });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                customerId,
                supplierId,
                amount,
                date,
                description
            };
            const res = await SettlementService.createSettlement(payload);
            if (res.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Mahsuplaşma işlemi başarıyla kaydedildi.', life: 3000 });
                setTimeout(() => navigate('/finance/settlements/list'), 1500);
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message, life: 3000 });
                setLoading(false);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İşlem sırasında bir hata oluştu.', life: 3000 });
            setLoading(false);
        }
    };

    const maxSettlementAmount = (customerDetail && supplierDetail)
        ? Math.min(Math.abs(customerDetail.summary.balance), Math.abs(supplierDetail.supplier.balance))
        : 0;

    return (
        <div className="max-w-5xl mx-auto p-2 animate-fade-in">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

            {/* COMPACT HEADER */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-lg shadow-indigo-200">
                        <i className="pi pi-sync text-lg" />
                    </div>
                    <div>
                        <h1 className="text-xl font-black text-slate-800 leading-tight">Cari Mahsuplaşma</h1>
                        <p className="text-xs text-slate-500">Hesapları birbirine virman ederek kapatın.</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button
                        label="Geçmiş"
                        icon="pi pi-history"
                        className="p-button-text p-button-secondary p-button-sm font-bold"
                        onClick={() => navigate('/finance/settlements/list')}
                    />
                    <Button
                        label="İptal"
                        icon="pi pi-times"
                        className="p-button-text p-button-danger p-button-sm font-bold"
                        onClick={() => navigate(-1)}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {/* LEFT: SELECTION & BALANCE */}
                <div className="md:col-span-8 space-y-4">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {/* CUSTOMER SELECT */}
                        <div className="space-y-3">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block">Müşteri Hesabı</label>
                            <CustomerAutoComplete
                                value={customerId}
                                onChange={setCustomerId}
                                className="w-full"
                                placeholder="Müşteri seçin..."
                            />
                            {customerDetail && (
                                <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 flex justify-between items-center">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] text-blue-600 font-bold uppercase">Müşteri Bakiyesi</span>
                                        <span className="text-lg font-black text-slate-800">
                                            {customerDetail.summary.balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                        </span>
                                    </div>
                                    <Tag
                                        value={customerDetail.summary.balance >= 0 ? 'BORÇLU' : 'ALACAKLI'}
                                        severity={customerDetail.summary.balance >= 0 ? 'warning' : 'success'}
                                        className="text-[10px] rounded-lg"
                                    />
                                </div>
                            )}
                        </div>

                        {/* SUPPLIER SELECT */}
                        <div className="space-y-3">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block">Tedarikçi Hesabı</label>
                            <SupplierAutoComplete
                                value={supplierId}
                                onChange={setSupplierId}
                                className="w-full"
                                placeholder="Tedarikçi seçin..."
                            />
                            {supplierDetail && (
                                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 flex justify-between items-center">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] text-emerald-600 font-bold uppercase">Tedarikçi Bakiyesi</span>
                                        <span className="text-lg font-black text-slate-800">
                                            {supplierDetail.supplier.balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                        </span>
                                    </div>
                                    <Tag
                                        value={supplierDetail.supplier.balance >= 0 ? 'BORÇLU' : 'ALACAKLI'}
                                        severity={supplierDetail.supplier.balance >= 0 ? 'danger' : 'success'}
                                        className="text-[10px] rounded-lg"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* DESCRIPTION & INFO */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-3">İşlem Açıklaması & Notlar</label>
                        <InputTextarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            autoResize
                            className="w-full !rounded-xl border-slate-200 text-sm"
                            placeholder="İşlem ile ilgili notunuzu buraya ekleyebilirsiniz..."
                        />

                        {customerId && supplierId && (
                            <div className="mt-4 flex items-start gap-3 p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                                <i className="pi pi-info-circle text-indigo-500 mt-1" />
                                <div className="text-xs text-indigo-700 leading-relaxed">
                                    <strong>Bakiye Eşitleme:</strong> Her iki hesapta da karşı işlem oluşturulacak ve
                                    vade takibi olan kalemler en eskiden başlanarak otomatik kapatılacaktır.
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT: ACTION PANEL */}
                <div className="md:col-span-4">
                    <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm h-full overflow-hidden flex flex-col">
                        <div className="p-6 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white">
                            <span className="text-[11px] font-black uppercase tracking-[0.2em] opacity-80 block mb-4">Mahsup Tutarı</span>
                            <div className="p-fluid">
                                <InputNumber
                                    value={amount}
                                    onValueChange={(e) => setAmount(e.value)}
                                    mode="currency"
                                    currency="TRY"
                                    locale="tr-TR"
                                    min={0}
                                    autoFocus
                                    className="w-full"
                                    inputClassName="!bg-white/10 !border-white/20 !text-white !font-black !text-3xl !h-16 !rounded-xl placeholder:text-white/30 text-center"
                                />
                            </div>

                            {maxSettlementAmount > 0 && (
                                <div className="mt-4 flex justify-between items-center text-xs">
                                    <span className="opacity-70">Önerilen Maksimum:</span>
                                    <button
                                        type="button"
                                        onClick={() => setAmount(maxSettlementAmount)}
                                        className="bg-white/20 hover:bg-white/30 transition-colors px-2 py-1 rounded-lg font-bold border-none text-white cursor-pointer"
                                    >
                                        {maxSettlementAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="p-6 space-y-6 flex-1 flex flex-col justify-between">
                            <div className="p-fluid">
                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-2">İşlem Tarihi</label>
                                <Calendar
                                    value={date}
                                    onChange={(e) => setDate(e.value)}
                                    showIcon
                                    className="w-full !rounded-xl"
                                    appendTo={mounted ? document.body : "self"}
                                />
                            </div>

                            <Button
                                label={loading ? "Kaydediliyor..." : "İşlemi Onayla"}
                                icon="pi pi-check-circle"
                                className="!bg-indigo-600 !border-indigo-600 !h-14 !rounded-2xl !font-black !text-lg shadow-lg shadow-indigo-100"
                                onClick={handleSave}
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettlementPage;
